import { IDynamicDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import {
    maxConcurrentConnectionsMetadata,
    partitionOptionConst,
    sqlWriteBatchSizeMetadata
} from '../consts/metadata-consts';
import { dataWarehouseAllowedSourceAuthTypes, isolationLevelConst, IsolationLevelType } from '../consts/sql-const';
import { azureSqlDWDatasetMetadata } from './azuresqldw-metadata';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ColumnPhysicalType, ColumnType, sqlBinaryPhysicalTypes } from 'app/shared/utils/column-type.util';
import { shellStringResources } from 'shell-models-lib';
import { getAppendOption, getSCD2Option, getUpsertOption } from '../consts/copy-job-const';

const physicalToPhysicalMapping: { [key: string]: string } = {
    datetime: ColumnPhysicalType.Datetime2,
    money: ColumnPhysicalType.Decimal,
    smalldatetime: ColumnPhysicalType.Datetime2,
    smallmoney: ColumnPhysicalType.Decimal,
    tinyint: ColumnPhysicalType.Int,
    binary: ColumnPhysicalType.Varbinary,
    nchar: ColumnPhysicalType.Char,
    nvarchar: ColumnPhysicalType.Varchar,
    datetimeoffset: ColumnPhysicalType.Varchar,
    timestamp: ColumnPhysicalType.Varbinary,
    xml: ColumnPhysicalType.Varchar,
    geography: ColumnPhysicalType.Varbinary,
    geometry: ColumnPhysicalType.Varbinary,
    hierarchyid: ColumnPhysicalType.Varchar,
    image: ColumnPhysicalType.Varbinary,
    text: ColumnPhysicalType.Varchar,
    ntext: ColumnPhysicalType.Varchar
};

const copyJobSettings = {
    fullLoadBehavior: {
        supportFullLoadBehavior: true
    },
    multipleQuery: true,
    sourcePartition: {
        supportSourcePartition: true
    }
};

export const dataWarehouseTableDatasetMetadata: IDynamicDatasetTypeMetadata = async () => {
    return {
        ...azureSqlDWDatasetMetadata,

        type: (__IS_FABRIC_DPE__ || __IS_FABRIC_MDF__) ? adfModelTypeMap.DataWarehouse.datasetType : adfModelTypeMap.Warehouse.datasetType,
        displayName: __IS_FABRIC_DPE__
            ? shellStringResources.Warehouse_DisplayText
            : shellStringResources.Warehouse_Fabric_DisplayText,

        datasetNamePrefix: 'WarehouseTable',
        icon: {
            className: 'svg-i_dataWarehouse'
        },

        copySourceControls: azureSqlDWDatasetMetadata.copySourceControls.map((c) => {
            if (c.child?.bind === 'isolationLevel') {
                return {
                    ...c,
                    child: {
                        ...c.child,
                        options: c.child.options.filter((o: ISelectOption<IsolationLevelType>) =>
                            [isolationLevelConst.Snapshot].includes(o.value)
                        )
                    }
                };
            } else if (c.bind === 'partitionOption') {
                return {
                    ...c,
                    options: c.options.filter((o: ISelectOption<IsolationLevelType>) =>
                        [partitionOptionConst.none, partitionOptionConst.dyamicRange].includes(o.value)
                    )
                };
            }
            return c;
        }),
        copySinkControls: (azureSqlDWDatasetMetadata.copySinkControls as ControlMetadata[]).filter(
            (c) => ![sqlWriteBatchSizeMetadata, maxConcurrentConnectionsMetadata].includes(c)
        ),
        supportLinkedServiceTypes: (__IS_FABRIC_DPE__ || __IS_FABRIC_MDF__)
            ? [adfModelTypeMap.DataWarehouse.linkedServiceType]
            : [adfModelTypeMap.Warehouse.linkedServiceType],
        supportDataFlowSink: true,
        supportDataFlowSource: true,
        supportDeleteActivity: false,
        notSupportTypeConversionWhenSink: true,
        supportTypeConversionWhenStaging: true,

        copyCommandAdditionalOptionProperties: [],
        allowedSourceAuthTypes: dataWarehouseAllowedSourceAuthTypes,
        allowedCopyMethods: __IS_FABRIC_DPE__ ? ['Insert', 'Upsert'] : [],
        LogicalToPhysicalMapping: {
            int16: ColumnPhysicalType.Smallint,
            int32: ColumnPhysicalType.Int,
            int64: ColumnPhysicalType.Bigint,
            byte: ColumnPhysicalType.Int,
            'byte[]': ColumnPhysicalType.Varbinary,
            // eslint-disable-next-line id-blacklist
            boolean: ColumnPhysicalType.Bit,
            // eslint-disable-next-line id-blacklist
            string: ColumnPhysicalType.Varchar,
            date: ColumnPhysicalType.Date,
            datetime: ColumnPhysicalType.Datetime2,
            datetimeoffset: ColumnPhysicalType.Varchar,
            decimal: ColumnPhysicalType.Decimal,
            'char[]': ColumnPhysicalType.Varchar,
            single: ColumnPhysicalType.Real,
            timespan: ColumnPhysicalType.Time,
            guid: ColumnPhysicalType.Uniqueidentifier,
            double: ColumnPhysicalType.Float,
            xml: ColumnPhysicalType.Varchar,
            sbyte: ColumnPhysicalType.Smallint,
            uint16: ColumnPhysicalType.Int,
            uint32: ColumnPhysicalType.Bigint,
            uint64: ColumnPhysicalType.Bigint
        },
        PhysicalToLogicalMapping: {
            bit: ColumnType.Boolean,
            smallint: ColumnType.Int16,
            int: ColumnType.Int32,
            bigint: ColumnType.Int64,
            date: {
                default: ColumnType.DateTime,
                Parquet: ColumnType.Date
            },
            datetime2: ColumnType.DateTime,
            time: ColumnType.TimeSpan,
            decimal: ColumnType.Decimal,
            numeric: ColumnType.Decimal,
            float: ColumnType.Double,
            real: ColumnType.Single,
            uniqueidentifier: ColumnType.Guid,
            varbinary: ColumnType.ByteArray,
            char: ColumnType.String,
            varchar: ColumnType.String
        },
        supportedPhysicalTypes: [
            ColumnPhysicalType.Bit,
            ColumnPhysicalType.Smallint,
            ColumnPhysicalType.Int,
            ColumnPhysicalType.Bigint,
            ColumnPhysicalType.Date,
            ColumnPhysicalType.Datetime2,
            ColumnPhysicalType.Time,
            ColumnPhysicalType.Decimal,
            ColumnPhysicalType.Numeric,
            ColumnPhysicalType.Float,
            ColumnPhysicalType.Real,
            ColumnPhysicalType.Uniqueidentifier,
            ColumnPhysicalType.Varbinary,
            ColumnPhysicalType.Char,
            ColumnPhysicalType.Varchar
        ],
        physicalToPhysicalMapping: {
            AzureSqlDWTable: physicalToPhysicalMapping,
            AzureSqlTable: physicalToPhysicalMapping,
            AzureSqlMITable: physicalToPhysicalMapping,
            SqlServerTable: physicalToPhysicalMapping,
            AmazonRdsForSqlServerTable: physicalToPhysicalMapping
        },
        binaryPhysicalTypes: sqlBinaryPhysicalTypes,
        binaryLengthMap: {
            varbinary: {
                minValue: 1,
                maxValue: 8000,
                supportMaxValue: __IS_FABRIC_DPE__,
                defaultValue: 8000
            },
            char: {
                minValue: 1,
                maxValue: 8000,
                supportMaxValue: false
            },
            varchar: {
                minValue: 1,
                maxValue: 8000,
                supportMaxValue: __IS_FABRIC_DPE__,
                defaultValue: 8000
            }
        },
        precisionMap: {
            datetime2: {
                minValue: 0,
                maxValue: 6
            },
            decimal: {
                minValue: 1,
                maxValue: 38
            },
            numeric: {
                minValue: 1,
                maxValue: 38
            }
        },
        scaleMap: {
            time: {
                minValue: 0,
                maxValue: 6
            },
            decimal: {
                minValue: 0,
                maxValue: 38,
                defaultValue: 18,
                smallerThanPrecision: true
            },
            numeric: {
                minValue: 0,
                maxValue: 38,
                defaultValue: 18,
                smallerThanPrecision: true
            }
        },
        copyJob: {
            updateMethod: {
                supportUpdateMethod: __IS_FABRIC_DPE__,
                options: [getAppendOption(), getUpsertOption(true), getSCD2Option()],
                updateMethodProperties: ['writeBehavior', 'upsertSettings'],
                defaultUpdateMethod: 'Append',
                upsertKeyColumnRequired: true
            },
            ...copyJobSettings
        }
    }
};
